import { Avatar, makeStyles, mergeClasses, shorthands, slot } from '@fluentui/react-components';
import { tokens, typographyStyles } from '@fluentui-copilot/tokens';
import type { GroundingMenuItemSlots, GroundingMenuItemState } from './GroundingMenuItem.types';
import type { SlotClassNames } from '@fluentui/react-components';
import { ACTIVEDESCENDANT_FOCUSVISIBLE_ATTRIBUTE } from '@fluentui/react-aria';
import { optionClassNames } from '@fluentui-copilot/react-input-listbox';

const FOCUS_VISIBLE_ATTR = 'data-fui-focus-visible';

export const groundingMenuItemClassNames: Omit<SlotClassNames<GroundingMenuItemSlots>, 'submenuButtonTooltip'> = {
  root: 'fai-GroundingMenuItem',
  persona: 'fai-GroundingMenuItem__persona',
  submenuButton: 'fai-GroundingMenuItem__submenuButton',
};

const focusRectStyles = {
  content: '""',
  position: 'absolute',
  pointerEvents: 'none',
  zIndex: 1,
  ...shorthands.border(tokens.strokeWidthThick, `solid`, tokens.colorStrokeFocus2),
  borderRadius: tokens.borderRadiusMedium,
  top: '-2px',
  bottom: '-2px',
  left: '-2px',
  right: '-2px',
};

/**
 * Styles for the root slot
 */
const useStyles = makeStyles({
  root: {
    position: 'relative',
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1Hover,
    },
    ':active:not(:has(button:active))': {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
      color: tokens.colorNeutralForeground1Pressed,
    },
    display: 'flex',
    flexDirection: 'row',
    alignContent: 'center',
    justifyContent: 'space-between',
    // If the persona slot has active-descendant or regular focus, apply focus rect to entire item
    [`& > .${groundingMenuItemClassNames.persona}:is([${FOCUS_VISIBLE_ATTR}], [${ACTIVEDESCENDANT_FOCUSVISIBLE_ATTRIBUTE}])::after`]:
      focusRectStyles,
  },
  secondaryText: { ...typographyStyles.caption2 },
  avatar: { marginRight: tokens.spacingHorizontalM },
  avatarImage: { backgroundColor: 'unset' },
  disabled: {
    color: tokens.colorNeutralForegroundDisabled,

    ':hover': {
      backgroundColor: tokens.colorTransparentBackground,
      color: tokens.colorNeutralForegroundDisabled,
    },

    ':active': {
      backgroundColor: tokens.colorTransparentBackground,
      color: tokens.colorNeutralForegroundDisabled,
    },

    '@media (forced-colors: active)': {
      color: 'GrayText',
    },
  },
  submenuButton: {
    height: '100%',
  },
  persona: {
    // Disable default focus rect in order to apply it to the entire item
    outline: 'none',
    flexGrow: 1,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalXS}`,
    paddingRight: tokens.spacingHorizontalNone,
  },
});

const useNextStyles = makeStyles({ root: { borderRadius: tokens.borderRadiusXLarge } });

/**
 * Apply styling to the GroundingMenuItem slots based on the state
 */
export const useGroundingMenuItemStyles_unstable = (state: GroundingMenuItemState): GroundingMenuItemState => {
  'use no memo';

  const { designVersion, disabled } = state;

  const styles = useStyles();
  const nextStyles = useNextStyles();
  state.root.className = mergeClasses(
    groundingMenuItemClassNames.root,
    styles.root,
    designVersion === 'next' && nextStyles.root,
    disabled && styles.disabled,
    state.root.className,
  );

  state.persona.className = mergeClasses(
    groundingMenuItemClassNames.persona,
    optionClassNames.root,
    styles.persona,
    state.persona.className,
  );

  if (state.persona.avatar) {
    const avatar = slot.always(state.persona.avatar, { elementType: Avatar });
    avatar.className = mergeClasses(styles.avatar, avatar.className);

    if (avatar.image) {
      const avatarImageSlot = slot.always(avatar.image, { elementType: 'img' });
      avatarImageSlot.className = mergeClasses(styles.avatarImage, avatarImageSlot.className);
      avatar.image = avatarImageSlot;
    }

    state.persona.avatar = avatar;
  }

  if (state.persona.secondaryText) {
    const secondaryText = slot.always(state.persona.secondaryText, { elementType: 'span' });
    secondaryText.className = mergeClasses(styles.secondaryText, secondaryText.className);
    state.persona.secondaryText = secondaryText;
  }

  if (state.submenuButton) {
    state.submenuButton.className = mergeClasses(
      groundingMenuItemClassNames.submenuButton,
      styles.submenuButton,
      state.submenuButton.className,
    );
  }

  return state;
};
